import { clstx } from '@payaca/utilities/cvaMerge';
import { motion, useReducedMotion } from 'motion/react';
import {
  FC,
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Slides to its content's height whenever the content resizes, so a row
 * added to or removed from a financial summary does not jolt everything below
 * it.
 */
const AnimatedHeight: FC<
  PropsWithChildren<{
    /** `span` for use where only inline content is allowed, e.g. a `label`. */
    as?: 'div' | 'span';
    className?: string;
  }>
> = (props) => {
  const { as = 'div', className, children } = props;
  const contentRef = useRef<HTMLDivElement & HTMLSpanElement>(null);
  const Outer = as === 'span' ? motion.span : motion.div;
  const Inner = as;
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const shouldReduceMotion = useReducedMotion();

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.borderBoxSize[0].blockSize);
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <Outer
      // Always clipped, as content resizes a frame before the slide starts. The
      // side padding keeps the clip clear of focus rings at the content's edge.
      className={clstx('-mx-2 block overflow-clip px-2', className)}
      initial={false}
      animate={{ height }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.2, ease: 'easeOut' }}
    >
      {/* flow-root keeps a child's margin inside the measured box. */}
      <Inner ref={contentRef} className="flow-root">
        {children}
      </Inner>
    </Outer>
  );
};

export default AnimatedHeight;
